/**
 * Codes tab — concept group codeset table.
 *
 * Excluded codes (e.g. "everything in this group except these specific
 * codes") render struck-through with a muted color so the inclusion vs.
 * exclusion semantics are scannable.
 */

import { Box, Chip, Stack, Typography } from '@mui/material';

import type { EpiArtifact } from '../../types/epi-chat-types';

export const CodesTab = ({ artifact }: { artifact: EpiArtifact }) => {
  const codes = artifact.codes ?? [];
  if (codes.length === 0) {
    return (
      <Box sx={{ padding: 2 }}>
        <Typography variant="body2" color="text.secondary">
          No codes yet. The codeset will appear here once the agent resolves the group.
        </Typography>
      </Box>
    );
  }
  // Hide the optional Concept ID column when no row populates it.
  const hasConceptIdColumn = codes.some((c) => c.conceptId);
  const includedCount = codes.filter((c) => !c.isExcluded).length;
  const excludedCount = codes.length - includedCount;

  return (
    <Stack spacing={1} sx={{ padding: 1.5 }} data-testid="artifact-codes-tab">
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography variant="caption" color="text.secondary">
          {`${codes.length} code${codes.length === 1 ? '' : 's'}`}
        </Typography>
        {excludedCount > 0 && (
          <Chip
            size="small"
            label={`${includedCount} included • ${excludedCount} excluded`}
            variant="outlined"
            sx={{ height: 20, fontSize: (theme) => theme.typography.caption.fontSize }}
          />
        )}
      </Stack>
      <Box
        sx={{
          overflow: 'auto',
          border: (theme) => `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
        }}
      >
        <Box
          component="table"
          sx={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: (theme) => theme.typography.body2.fontSize,
            fontFamily: 'monospace',
          }}
        >
          <Box component="thead">
            <Box component="tr">
              <Box
                component="th"
                sx={{
                  textAlign: 'left',
                  paddingX: 1,
                  paddingY: 0.75,
                  borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                  backgroundColor: (theme) => theme.palette.action.hover,
                  fontWeight: 600,
                }}
              >
                Code
              </Box>
              <Box
                component="th"
                sx={{
                  textAlign: 'left',
                  paddingX: 1,
                  paddingY: 0.75,
                  borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                  backgroundColor: (theme) => theme.palette.action.hover,
                  fontWeight: 600,
                }}
              >
                Vocab
              </Box>
              {hasConceptIdColumn && (
                <Box
                  component="th"
                  sx={{
                    textAlign: 'left',
                    paddingX: 1,
                    paddingY: 0.75,
                    borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                    backgroundColor: (theme) => theme.palette.action.hover,
                    fontWeight: 600,
                  }}
                >
                  Concept ID
                </Box>
              )}
              <Box
                component="th"
                sx={{
                  textAlign: 'left',
                  paddingX: 1,
                  paddingY: 0.75,
                  borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                  backgroundColor: (theme) => theme.palette.action.hover,
                  fontWeight: 600,
                }}
              >
                Description
              </Box>
            </Box>
          </Box>
          <Box component="tbody">
            {codes.map((row) => (
              <Box
                component="tr"
                key={`${row.vocabulary}:${row.code}`}
                sx={{
                  '&:nth-of-type(even)': {
                    backgroundColor: (theme) => theme.palette.action.hover,
                  },
                  ...(row.isExcluded
                    ? {
                        textDecoration: 'line-through',
                        color: (theme) => theme.palette.text.disabled,
                      }
                    : {}),
                }}
              >
                <Box
                  component="td"
                  sx={{
                    paddingX: 1,
                    paddingY: 0.5,
                    borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                  }}
                >
                  {row.code}
                </Box>
                <Box
                  component="td"
                  sx={{
                    paddingX: 1,
                    paddingY: 0.5,
                    borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                  }}
                >
                  {row.vocabulary}
                </Box>
                {hasConceptIdColumn && (
                  <Box
                    component="td"
                    sx={{
                      paddingX: 1,
                      paddingY: 0.5,
                      borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    {row.conceptId ?? '—'}
                  </Box>
                )}
                <Box
                  component="td"
                  sx={{
                    paddingX: 1,
                    paddingY: 0.5,
                    borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                    fontFamily: (theme) => theme.typography.body2.fontFamily,
                  }}
                >
                  {row.description ?? ''}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Stack>
  );
};
